import { useCallback } from 'react';
import type { CropArea } from './types';
import { createLogger } from '@/shared/lib/logger';
import type { ImageSizePolicy } from '@/shared/lib/services/image-upload.service';
import { cropImageToJpeg } from './default-crop';

const logger = createLogger('useCropCanvas');

interface UseCropCanvasProps {
  /**
   * Shared downscale + 16px-align policy (angle ~2MP by area, reference 768px
   * long side, control ~1MP). Same source of truth as the upload service so the
   * cropped output never needs a second resize downstream.
   */
  sizePolicy: Pick<ImageSizePolicy, 'mode' | 'target'>;
  /** JPEG output size budget in bytes. Defaults to 900KB. */
  maxFileSize?: number;
}

export interface CroppedImageResult {
  /**
   * Instant, synchronous data-URL of the cropped frame — usable as an
   * optimistic placeholder the moment the user confirms, before the (async)
   * JPEG size-budget compression below finishes. `null` when not requested.
   */
  previewUrl: string | null;
  /**
   * Background JPEG compression of the same frame to the size budget. Runs on
   * a detached canvas, so it keeps resolving even after the crop modal that
   * triggered it has unmounted.
   */
  blobPromise: Promise<Blob | null>;
}

interface UseCropCanvasReturn {
  cropImage: (
    imageElement: HTMLImageElement | null,
    cropArea: CropArea,
    options?: { emitPreview?: boolean }
  ) => CroppedImageResult;
}

/**
 * Crops + downscales an image to a detached canvas. The pixels are captured
 * synchronously (so the source object URL can be revoked / the modal can close
 * right away), while the final JPEG is compressed in the background.
 */
export function useCropCanvas({
  sizePolicy,
  maxFileSize = 900 * 1024,
}: UseCropCanvasProps): UseCropCanvasReturn {
  const cropImage = useCallback(
    (
      imageElement: HTMLImageElement | null,
      cropArea: CropArea,
      options?: { emitPreview?: boolean }
    ): CroppedImageResult => {
      const empty: CroppedImageResult = {
        previewUrl: null,
        blobPromise: Promise.resolve(null),
      };

      if (!imageElement) {
        console.error('Image not ready');
        return empty;
      }

      // Detached canvas (not mounted in the DOM) so compression survives the
      // crop modal closing immediately on confirm — same trick as the rotate
      // path. The drawImage below is synchronous, so the pixels are safely
      // captured before the caller revokes the source object URL.
      const cropped = cropImageToJpeg(
        imageElement,
        cropArea,
        sizePolicy,
        maxFileSize
      );
      if (!cropped) {
        console.error('Failed to get canvas context');
        return empty;
      }
      const { canvas } = cropped;

      const previewUrl = options?.emitPreview
        ? canvas.toDataURL('image/jpeg', 0.7)
        : null;

      const blobPromise = cropped.blobPromise.then((blob) => {
        if (blob) {
          logger.info('Cropped image created:', {
            targetDimensions: `${canvas.width}x${canvas.height}`,
            pixelCount: canvas.width * canvas.height,
            fileSize: blob.size,
            type: blob.type,
          });
        } else {
          console.error('Failed to create blob from canvas');
        }
        return blob;
      });

      return { previewUrl, blobPromise };
    },
    [sizePolicy, maxFileSize]
  );

  return { cropImage };
}
